/**
 * Tibbs at the table, exactly like Single Didge's Readout and Medal Ceremony
 * (docs/SINGLE-DIDGE-READOUT-ANALYSIS.md; docs/TIBBS-VOICE.md 2): nothing
 * auto-plays. "Hear Tibbs" on the hole-end sheet asks for a readout of that
 * hole, written live by Claude about these players' actual scores and voiced
 * live by Cartesia; "Presentation of the Golden Amphora" on the game-over
 * screen asks for the presentation.
 *
 * - Vs computer: the device POSTs the hole to /api/readout and plays the
 *   framed stream (each chunk's words, then its MP3) as it arrives. The game's
 *   memory (who went out each hole, the last scripts, running gags, jokes
 *   already used) is kept on the device, per game.
 * - Online: the device asks the room, which writes and voices it once; every
 *   device starts together at the room's start time and later requests replay
 *   for the requester only. The room keeps the memory.
 * - A readout already heard on this device replays from memory, free.
 * - Offline, or a server without the keys, the buttons are hidden (Tibbs
 *   needs a connection, as Single Didge's announcer does).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GameState } from '../../engine';
import type { TibbsPose } from '../components/Art';
import type { SeatInfo } from '../game/types';
import type { ReadoutEvent } from '../game/useRoom';
import { KEYS, load, save } from '../lib/storage';
import { reducedMotion } from '../lib/motion';
import { unlockAudio } from '../lib/sound';
import { cleanGags, cleanPreviousScripts, readoutRequestFromState, recordOut } from '../../readout/facts';
import { FRAME, FRAMES_TYPE, FrameDecoder, cleanMotifs, loadingMessageAt, LOADING_MESSAGES, motifsUsedIn } from '../../readout/script';
import { podiumSeats, revealAt, scriptSentences } from './ceremony';
import type { Podium, Reveal } from './ceremony';
import { normalizeTibbsSettings, voiceGain } from './settings';
import type { TibbsSettings } from './settings';
import { playQueue, stopTibbs, warmMusic } from './voicePlayer';
import type { PlayState, QueueCtl, QueueItem } from './voicePlayer';

export const MUSIC_URL = '/audio/ceremony-largo.mp3';
/** Seconds of Largo alone before Tibbs opens the presentation (a phrase boundary in the recording). */
export const CEREMONY_SOLO_S = 7;
/** How long a tapped request waits for the room to acknowledge it (Single Didge's READOUT_ACK_MS). */
export const ACK_MS = 12_000;

export type TibbsStatus = 'idle' | 'loading' | 'playing' | 'blocked' | 'done' | 'failed';

export interface TibbsCue {
  caption: string;
  /** Players named in the sentence on screen (their rows glow). */
  seats: number[];
  pose: TibbsPose;
}

export interface TibbsApi {
  /** False offline or when the server has no voice: hide the buttons. */
  enabled: boolean;
  status: (key: string) => TibbsStatus;
  /** The rotating "Tibbs is clearing his throat…" line while a readout is being written. */
  loadingText: string;
  playingKey: string | null;
  cue: TibbsCue | null;
  ceremonyOpen: boolean;
  podium: Podium | null;
  reveal: Reveal;
  request: (key: string) => void;
  stop: () => void;
  resume: () => void;
  skipCeremony: () => void;
  closeCeremony: () => void;
  settings: TibbsSettings;
  setSettings: (s: TibbsSettings) => void;
}

/** What this device remembers about one game's readouts (vs computer). */
export interface TibbsMemory {
  gameId: string;
  outs: (number | null)[];
  /** Scripts by key, in the order heard. */
  scripts: [string, string][];
  gags: Record<string, string>;
  motifs: string[];
}

export function emptyMemory(gameId: string): TibbsMemory {
  return { gameId, outs: [], scripts: [], gags: {}, motifs: [] };
}

export function loadMemory(gameId: string): TibbsMemory {
  const m = load<TibbsMemory | null>(KEYS.tibbsMemory, null);
  if (!m || m.gameId !== gameId) return emptyMemory(gameId);
  return {
    gameId,
    outs: Array.isArray(m.outs) ? m.outs.map((o) => (Number.isInteger(o) ? (o as number) : null)) : [],
    scripts: Array.isArray(m.scripts) ? m.scripts.filter((x) => Array.isArray(x) && typeof x[0] === 'string' && typeof x[1] === 'string').slice(-24) : [],
    gags: cleanGags(m.gags),
    motifs: cleanMotifs(m.motifs),
  };
}

/** Fold a finished script into the memory (once per key). */
export function remember(m: TibbsMemory, key: string, data: { script?: string; gags?: unknown; motifs?: unknown }): TibbsMemory {
  if (m.scripts.some(([k]) => k === key)) return m;
  const script = String(data.script ?? '');
  return {
    ...m,
    scripts: script ? [...m.scripts, [key, script] as [string, string]].slice(-24) : m.scripts,
    gags: { ...m.gags, ...cleanGags(data.gags) },
    motifs: cleanMotifs([...m.motifs, ...(Array.isArray(data.motifs) ? data.motifs : motifsUsedIn(script))]),
  };
}

// ---------------------------------------------------------------------------
// The server's config, asked once (and again when the connection comes back).

let configP: Promise<boolean> | null = null;
/** The last answer, so a later table renders the buttons on its very first frame. */
let knownEnabled: boolean | null = null;
function readoutEnabled(): Promise<boolean> {
  if (!configP) {
    configP = fetch('/api/readout/config', { cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<{ enabled?: boolean }>) : { enabled: false }))
      .then((c) => (knownEnabled = Boolean(c.enabled)))
      .catch(() => {
        configP = null;
        return false;
      });
  }
  return configP;
}

/** Set what the server said (tests; or a page that already knows). */
export function primeReadoutConfig(enabled: boolean | null): void {
  knownEnabled = enabled;
  configP = enabled === null ? null : Promise.resolve(enabled);
}

// ---------------------------------------------------------------------------
// Readouts heard on this device: replays are free (Single Didge's S.readoutAudio).

interface Heard {
  items: QueueItem[];
  done: boolean;
}
const heard = new Map<string, Heard>();

/** A vs-computer generation in flight or finished (Single Didge's readoutStreamJob). */
interface SoloJob {
  items: QueueItem[];
  ctl: QueueCtl | null;
  done: boolean;
  error: Error | null;
}

export function useTibbs(opts: {
  state: GameState;
  seats: SeatInfo[];
  me: number | null;
  mode: 'solo' | 'online';
  gameId: string;
  guided: boolean;
  muted: boolean;
  requestOnline?: (key: string) => boolean;
  roomEvents?: ReadoutEvent[];
  notify?: (text: string) => void;
}): TibbsApi {
  const { state, seats, me, mode, gameId, guided, muted } = opts;
  const [settings, setSettingsState] = useState<TibbsSettings>(() => normalizeTibbsSettings(load(KEYS.tibbs, null)));
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  // Until the server answers, online tables show the button (the room says clearly if Tibbs cannot speak); vs computer waits.
  const [enabled, setEnabled] = useState(() => knownEnabled ?? mode === 'online');
  const [statuses, setStatuses] = useState<Record<string, TibbsStatus>>({});
  const statusRef = useRef(statuses);
  statusRef.current = statuses;
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [cue, setCue] = useState<TibbsCue | null>(null);
  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const [saying, setSaying] = useState(-1);
  const [texts, setTexts] = useState<string[]>([]);
  const [loadingTick, setLoadingTick] = useState(0);
  const loadingStart = useRef({ at: 0, offset: 0 });
  const ctlRef = useRef<QueueCtl | null>(null);
  const jobs = useRef(new Map<string, SoloJob>());
  const ackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unconfirmed = useRef<string | null>(null);
  const memRef = useRef<TibbsMemory>(loadMemory(gameId));
  const stateRef = useRef(state);
  stateRef.current = state;
  const notifyRef = useRef(opts.notify);
  notifyRef.current = opts.notify;

  // Is Tibbs available? Asked once; again when the connection comes back.
  useEffect(() => {
    if (guided) return;
    let live = true;
    const ask = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return setEnabled(false);
      void readoutEnabled().then((on) => live && setEnabled(on));
    };
    ask();
    const offline = () => setEnabled(false);
    window.addEventListener('online', ask);
    window.addEventListener('offline', offline);
    return () => {
      live = false;
      window.removeEventListener('online', ask);
      window.removeEventListener('offline', offline);
    };
  }, [guided]);

  // A new game on this device: fresh memory and statuses.
  useEffect(() => {
    memRef.current = loadMemory(gameId);
    jobs.current.clear();
    setStatuses({});
    setCeremonyOpen(false);
  }, [gameId]);

  const name = useCallback((seat: number) => seats[seat]?.name || state.players[seat]?.name || `Player ${seat + 1}`, [seats, state.players]);
  const nameRef = useRef(name);
  nameRef.current = name;

  // Record who went out as each hole ends (vs computer; the room keeps its own).
  const over = state.phase === 'holeOver' || state.phase === 'gameOver';
  useEffect(() => {
    if (!over || mode !== 'solo' || guided) return;
    const m = memRef.current;
    const outs = recordOut(m.outs, stateRef.current);
    if (JSON.stringify(outs) !== JSON.stringify(m.outs)) {
      memRef.current = { ...m, outs };
      save(KEYS.tibbsMemory, memRef.current);
    }
  }, [over, state.hole, mode, guided]);

  const setStatus = useCallback((key: string, s: TibbsStatus) => {
    setStatuses((m) => (m[key] === s ? m : { ...m, [key]: s }));
  }, []);

  // The loading line rotates while anything is being written.
  const anyLoading = Object.values(statuses).includes('loading');
  useEffect(() => {
    if (!anyLoading) return;
    loadingStart.current = { at: Date.now(), offset: Math.floor(Math.random() * LOADING_MESSAGES.length) };
    const t = setInterval(() => setLoadingTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [anyLoading]);
  const loadingText = useMemo(
    () => loadingMessageAt(Date.now() - loadingStart.current.at, { offset: loadingStart.current.offset, reducedMotion: reducedMotion() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingTick, anyLoading],
  );

  // The table moved on (next hole, new game): Tibbs stops mid-sentence, fading out.
  const phaseKey = `${gameId}:${state.hole}:${over}`;
  const playingRef = useRef<string | null>(null);
  playingRef.current = playingKey;
  useEffect(() => {
    if (playingRef.current && playingRef.current !== 'final') stopTibbs();
  }, [phaseKey]);
  useEffect(() => () => stopTibbs(), []);

  /** The sentence on screen: caption, who it names, and Tibbs's pose. */
  const cueFor = useCallback((sentence: string): TibbsCue => {
    const st = stateRef.current;
    const named = st.players.map((_, i) => i).filter((i) => {
      const n = nameRef.current(i).trim();
      return n.length > 0 && new RegExp(`(^|[^\\p{L}])${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^\\p{L}]|$)`, 'iu').test(sentence);
    });
    const pose: TibbsPose = /\boh,? dear\b|large number|unpleasantness|\bhm+\b/i.test(sentence)
      ? 'retract'
      : /amphora|champion/i.test(sentence)
        ? 'celebrate'
        : /remarkable|quite something|well played|cap off|astonish/i.test(sentence)
          ? 'applaud'
          : named.length
            ? 'point'
            : 'wry';
    return { caption: sentence, seats: named, pose };
  }, []);

  /** Start the queue player for a key; returns its controller. */
  const begin = useCallback(
    (key: string, delayMs: number): QueueCtl => {
      setPlayingKey(key);
      setSaying(-1);
      setTexts([]);
      setCue(null);
      const ctl = playQueue({
        gain: voiceGain(settingsRef.current, mutedRef.current),
        delayMs,
        music: key === 'final' ? { url: MUSIC_URL, soloS: CEREMONY_SOLO_S } : null,
        onState: (s: PlayState) => {
          if (s === 'loading') return;
          if (s === 'done' || s === 'stopped') {
            setStatus(key, 'done');
            if (ctlRef.current === ctl) {
              ctlRef.current = null;
              setCue(null);
            }
          } else setStatus(key, s);
        },
        onProgress: (p) => {
          setSaying(p.globalSentence);
          setCue(cueFor(p.sentence));
        },
      });
      const push = ctl.push.bind(ctl);
      ctl.push = (item: QueueItem) => {
        setTexts((t) => [...t, item.text]);
        push(item);
      };
      ctlRef.current = ctl;
      return ctl;
    },
    [cueFor, setStatus],
  );

  /** Replay a readout heard on this device. */
  const playHeard = useCallback(
    (key: string, h: Heard) => {
      const ctl = begin(key, 0);
      for (const it of h.items) ctl.push(it);
      if (h.done) ctl.end();
    },
    [begin],
  );

  const heardKey = (key: string) => `${gameId}:${key}`;

  /** Vs computer: one generation per hole (POST, framed stream), shared by every press. */
  const soloJob = useCallback(
    (key: string): SoloJob => {
      const existing = jobs.current.get(key);
      if (existing && !existing.error) return existing;
      const job: SoloJob = { items: [], ctl: null, done: false, error: null };
      jobs.current.set(key, job);
      const m = memRef.current;
      const body = readoutRequestFromState(stateRef.current, {
        gameId,
        names: stateRef.current.players.map((_, i) => nameRef.current(i)),
        firstSeat: me,
        outs: m.outs,
        previousScripts: cleanPreviousScripts(m.scripts.filter(([k]) => k !== key).map(([, s]) => s).slice(-2)),
        gags: m.gags,
        usedMotifs: m.motifs,
      });
      const fail = (e: Error) => {
        job.error = e;
        job.ctl?.stop();
        setStatus(key, 'failed');
        notifyRef.current?.(e.message);
      };
      const finish = () => {
        job.done = true;
        heard.set(heardKey(key), { items: job.items.slice(), done: true });
        job.ctl?.end();
      };
      const keep = (data: { script?: string; gags?: unknown; motifs?: unknown }) => {
        memRef.current = remember(memRef.current, key, data);
        save(KEYS.tibbsMemory, memRef.current);
      };
      void (async () => {
        try {
          const res = await fetch('/api/readout', {
            method: 'POST',
            headers: { 'content-type': 'application/json', accept: `${FRAMES_TYPE}, audio/mpeg` },
            body: JSON.stringify(body),
          });
          if (!res.ok) {
            const err = (await res.json().catch(() => ({}))) as { error?: string };
            throw new Error(err.error || 'Tibbs lost his voice. Try again.');
          }
          if ((res.headers.get('content-type') || '').includes(FRAMES_TYPE) && res.body) {
            const reader = res.body.getReader();
            const decoder = new FrameDecoder();
            let text = '';
            for (;;) {
              const { value, done } = await reader.read();
              if (done) break;
              for (const fr of decoder.push(value)) {
                if (fr.type === FRAME.TEXT) text = String(fr.data?.text ?? '');
                else if (fr.type === FRAME.AUDIO) {
                  const item = { bytes: fr.bytes, text };
                  job.items.push(item);
                  job.ctl?.push(item);
                  text = '';
                } else if (fr.type === FRAME.META) keep(fr.data ?? {});
                else if (fr.type === FRAME.END) finish();
                else if (fr.type === FRAME.ERROR) throw new Error(String(fr.data?.error ?? 'Tibbs lost his voice. Try again.'));
              }
            }
            if (!job.done) throw new Error('Tibbs cut out. Try again.');
          } else {
            // A cached replay from the server: one MP3, with its script and chunk words in headers.
            const read = (h: string) => {
              try {
                return JSON.parse(decodeURIComponent(res.headers.get(h) || 'null')) as unknown;
              } catch {
                return decodeURIComponent(res.headers.get(h) || '');
              }
            };
            const script = String(read('x-readout-script') ?? '');
            keep({ script, gags: read('x-readout-gags'), motifs: read('x-readout-motifs') });
            const words = read('x-readout-chunks-text');
            const item = { bytes: new Uint8Array(await res.arrayBuffer()), text: Array.isArray(words) ? words.join(' ') : script };
            job.items.push(item);
            job.ctl?.push(item);
            finish();
          }
        } catch (e) {
          fail(e instanceof Error ? e : new Error('Tibbs lost his voice. Try again.'));
        }
      })();
      return job;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gameId, me, setStatus],
  );

  const request = useCallback(
    (key: string) => {
      unlockAudio();
      const st = statusRef.current[key];
      if (st === 'loading' || st === 'playing') return;
      if (key === 'final') {
        setCeremonyOpen(true);
        warmMusic(MUSIC_URL);
      }
      const h = heard.get(heardKey(key));
      if (h?.done) return playHeard(key, h);
      if (mode === 'online' && opts.requestOnline) {
        // Show the wait at once; the room answers with a status that puts the whole table in it.
        setStatus(key, 'loading');
        unconfirmed.current = key;
        opts.requestOnline(key);
        if (ackTimer.current) clearTimeout(ackTimer.current);
        ackTimer.current = setTimeout(() => {
          if (unconfirmed.current !== key || statusRef.current[key] !== 'loading') return;
          unconfirmed.current = null;
          setStatus(key, 'failed');
          notifyRef.current?.("Couldn't reach Tibbs. Try again.");
        }, ACK_MS);
        return;
      }
      const job = soloJob(key);
      setStatus(key, 'loading');
      const ctl = begin(key, 0);
      job.ctl = ctl;
      for (const it of job.items) ctl.push(it);
      if (job.done) ctl.end();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, opts.requestOnline, playHeard, soloJob, begin, setStatus, gameId],
  );

  // Online: the room's readout messages (anyone's request, ours included).
  const lastEvent = useRef(0);
  const roomCtl = useRef<{ key: string; hash: string; ctl: QueueCtl; items: QueueItem[] } | null>(null);
  useEffect(() => {
    for (const ev of opts.roomEvents ?? []) {
      if (ev.id <= lastEvent.current) continue;
      lastEvent.current = ev.id;
      const st = stateRef.current;
      const current = st.phase === 'gameOver' ? 'final' : st.phase === 'holeOver' ? `h${st.hole}` : null;
      if (ev.key !== current) continue;
      if (ev.t === 'readoutStatus') {
        if (ev.status === 'generating') {
          if (unconfirmed.current === ev.key) unconfirmed.current = null;
          const waiting = statusRef.current[ev.key] === 'loading';
          if (!waiting && ev.seat !== null && ev.seat !== me) notifyRef.current?.(`${nameRef.current(ev.seat)} asked Tibbs for ${ev.key === 'final' ? 'the presentation' : 'his readout'}`);
          setStatus(ev.key, 'loading');
          if (ev.key === 'final') warmMusic(MUSIC_URL);
        } else if (ev.status === 'failed') {
          unconfirmed.current = null;
          setStatus(ev.key, 'failed');
          notifyRef.current?.(ev.message || 'Tibbs lost his voice. Try again.');
        } else if (ev.status === 'idle') {
          // Back on the socket and nothing is on the way (the room restarted mid-write): stop waiting.
          if (statusRef.current[ev.key] !== 'loading' || unconfirmed.current === ev.key) continue;
          setStatus(ev.key, 'failed');
          notifyRef.current?.('Tibbs lost his place. Tap to try again.');
        }
      } else if (ev.t === 'readout') {
        unconfirmed.current = null;
        if (ev.key === 'final') setCeremonyOpen(true);
        const delay = ev.replay ? 0 : Math.max(0, Math.min(10_000, ev.startAt - ev.now));
        const ctl = begin(ev.key, delay);
        const items: QueueItem[] = [];
        roomCtl.current = { key: ev.key, hash: ev.hash, ctl, items };
        if (ev.chunks) {
          for (const c of ev.chunks) {
            const it = { url: c.url, text: c.text };
            items.push(it);
            ctl.push(it);
          }
          if (ev.total !== null && ev.total !== undefined && ev.chunks.length >= ev.total) ctl.end();
        } else if (ev.url) {
          const it = { url: ev.url, text: ev.script ?? '' };
          items.push(it);
          ctl.push(it);
          ctl.end();
          heard.set(heardKey(ev.key), { items: items.slice(), done: true });
        }
      } else if (ev.t === 'readoutChunk') {
        const r = roomCtl.current;
        if (r && r.key === ev.key && r.hash === ev.hash) {
          const it = { url: ev.chunk.url, text: ev.chunk.text };
          r.items.push(it);
          r.ctl.push(it);
        }
      } else if (ev.t === 'readoutDone') {
        const r = roomCtl.current;
        if (r && r.key === ev.key && r.hash === ev.hash) {
          r.ctl.end();
          heard.set(heardKey(ev.key), { items: r.items.slice(), done: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.roomEvents]);

  const stop = useCallback(() => ctlRef.current?.stop(), []);
  const resume = useCallback(() => ctlRef.current?.resume(), []);
  const skipCeremony = useCallback(() => {
    ctlRef.current?.stop();
    setStatus('final', 'done');
  }, [setStatus]);
  const closeCeremony = useCallback(() => {
    ctlRef.current?.stop();
    setCeremonyOpen(false);
  }, []);
  const setSettings = useCallback(
    (s: TibbsSettings) => {
      setSettingsState(s);
      save(KEYS.tibbs, s);
      ctlRef.current?.setGain(voiceGain(s, mutedRef.current));
    },
    [],
  );
  useEffect(() => {
    ctlRef.current?.setGain(voiceGain(settingsRef.current, muted));
  }, [muted]);

  const podium = useMemo(() => (state.phase === 'gameOver' ? podiumSeats(state) : null), [state]);
  const finalStatus = statuses.final ?? 'idle';
  const reveal = useMemo(
    () => (podium ? revealAt(scriptSentences(texts), playingKey === 'final' ? saying : -1, finalStatus === 'done' || finalStatus === 'idle' || finalStatus === 'failed', podium) : { crest: false, last: false, bronze: false, silver: false, gold: false }),
    [podium, texts, saying, playingKey, finalStatus],
  );

  return useMemo(
    () => ({
      enabled: enabled && !guided,
      status: (key: string) => statuses[key] ?? 'idle',
      loadingText,
      playingKey,
      cue,
      ceremonyOpen,
      podium,
      reveal,
      request,
      stop,
      resume,
      skipCeremony,
      closeCeremony,
      settings,
      setSettings,
    }),
    [enabled, guided, statuses, loadingText, playingKey, cue, ceremonyOpen, podium, reveal, request, stop, resume, skipCeremony, closeCeremony, settings, setSettings],
  );
}
